import { Check, X } from "lucide-react";

const comparisons = [
  {
    aspect: "Feedback Frequency",
    traditional: "Annual engagement survey",
    sendfeedback: "Continuous pulse on culture health",
  },
  {
    aspect: "True Anonymity",
    traditional: "\"Anonymous\" with metadata tracking",
    sendfeedback: "Legal-grade anonymity — even HR can't identify",
  },
  {
    aspect: "Time to Insights",
    traditional: "Weeks of manual analysis",
    sendfeedback: "Instant AI-powered analysis",
  },
  {
    aspect: "Board / C-Suite Reporting",
    traditional: "Manual slide deck creation",
    sendfeedback: "Auto-generated executive dashboards",
  },
  {
    aspect: "Exit Interview Insights",
    traditional: "Biased post-departure feedback",
    sendfeedback: "Honest feedback before they quit",
  },
  {
    aspect: "HR Team Workload",
    traditional: "Weeks analyzing spreadsheets",
    sendfeedback: "AI does analysis — HR takes action",
  },
  {
    aspect: "Compliance Documentation",
    traditional: "DIY documentation",
    sendfeedback: "Audit-ready anonymity architecture",
  },
  {
    aspect: "Setup Time",
    traditional: "Weeks with consultants",
    sendfeedback: "2 minutes, no IT needed",
  },
];

export const ComparisonTable = () => {
  return (
    <section className="py-20 md:py-28 px-4 sm:px-6 md:px-10">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-12 md:mb-16 max-w-3xl">
          <div className="flex items-center gap-2 mb-5">
            <span className="inline-flex h-2 w-2 rounded-full bg-primary" />
            <span className="font-sans text-[11px] uppercase tracking-[0.22em] font-medium text-muted-foreground">
              The Comparison
            </span>
          </div>
          <h2 className="font-sans text-3xl md:text-5xl font-medium tracking-[-0.02em] text-foreground leading-[1.1]">
            Why People leaders choose{" "}
            <span className="font-serif italic font-normal text-primary">SendFeedback.</span>
          </h2>
          <p className="font-sans text-[15px] md:text-[17px] text-muted-foreground mt-5 leading-relaxed">
            Traditional surveys weren&rsquo;t built for the way modern teams actually work — or for the level of trust honest feedback requires.
          </p>
        </div>

        {/* Desktop ledger */}
        <div className="hidden md:block rounded-[28px] border border-border/60 bg-card/90 backdrop-blur-sm overflow-hidden shadow-[0_24px_60px_-32px_rgba(62,92,77,0.18)]">
          <div className="grid grid-cols-12 px-7 py-5 border-b border-border/60 bg-secondary/30">
            <div className="col-span-4 font-sans text-[11px] uppercase tracking-[0.22em] font-medium text-muted-foreground">
              Criterion
            </div>
            <div className="col-span-4 font-sans text-[11px] uppercase tracking-[0.22em] font-medium text-muted-foreground">
              Traditional Surveys
            </div>
            <div className="col-span-4 font-sans text-[11px] uppercase tracking-[0.22em] font-medium text-primary flex items-center gap-2">
              <span className="inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
              SendFeedback
            </div>
          </div>

          {comparisons.map((row, index) => (
            <div
              key={row.aspect}
              className={`group grid grid-cols-12 items-start px-7 py-6 transition-colors hover:bg-secondary/20 ${
                index !== comparisons.length - 1 ? "border-b border-border/40" : ""
              }`}
            >
              <div className="col-span-4 flex items-start gap-4 pr-6">
                <span className="font-sans text-[11px] font-medium tabular-nums text-muted-foreground/70 mt-1">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="font-sans text-[15px] md:text-base font-medium tracking-[-0.005em] text-foreground leading-snug">
                  {row.aspect}
                </span>
              </div>
              <div className="col-span-4 flex items-start gap-3 pr-6">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-muted text-muted-foreground/80 flex-shrink-0 mt-0.5">
                  <X className="h-3.5 w-3.5" strokeWidth={2} />
                </span>
                <span className="font-sans text-[14.5px] text-muted-foreground leading-relaxed">
                  {row.traditional}
                </span>
              </div>
              <div className="col-span-4 flex items-start gap-3">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/12 text-primary flex-shrink-0 mt-0.5 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                </span>
                <span className="font-sans text-[14.5px] text-foreground leading-relaxed">
                  {row.sendfeedback}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-3">
          {comparisons.map((row, index) => (
            <div
              key={row.aspect}
              className="rounded-2xl border border-border/60 bg-card/90 backdrop-blur-sm p-5"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="font-sans text-[11px] font-medium tabular-nums text-muted-foreground/70">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h4 className="font-sans text-[15px] font-medium tracking-[-0.005em] text-foreground leading-tight">
                  {row.aspect}
                </h4>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3 rounded-xl bg-muted/40 p-3">
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-muted text-muted-foreground flex-shrink-0 mt-0.5">
                    <X className="h-3 w-3" strokeWidth={2} />
                  </span>
                  <div>
                    <div className="font-sans text-[10px] uppercase tracking-[0.2em] font-medium text-muted-foreground mb-1">
                      Traditional
                    </div>
                    <span className="font-sans text-[14px] text-muted-foreground leading-relaxed">
                      {row.traditional}
                    </span>
                  </div>
                </div>

                <div className="flex items-start gap-3 rounded-xl bg-primary/[0.06] p-3">
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary/15 text-primary flex-shrink-0 mt-0.5">
                    <Check className="h-3 w-3" strokeWidth={2.5} />
                  </span>
                  <div>
                    <div className="font-sans text-[10px] uppercase tracking-[0.2em] font-medium text-primary mb-1">
                      SendFeedback
                    </div>
                    <span className="font-sans text-[14px] text-foreground leading-relaxed">
                      {row.sendfeedback}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
